/* Premium variant picker
 * Drives the radio-based selector rendered by snippets/product-variant-picker.liquid.
 * - Matches selected option values to a variant
 * - Updates price / compare-at / Add-to-cart state
 * - Disables impossible combinations and flags sold-out values
 * - Reflects the selected value in each option legend
 */
(() => {
  const picker = document.querySelector('[data-variant-picker]');
  if (!picker) return;

  const idSelect = picker.querySelector('[data-variant-id]');
  const variantsEl = picker.querySelector('[data-variants-json]');
  const priceEl = document.querySelector('[data-pdp-price]');
  const compareEl = document.querySelector('[data-pdp-compare]');

  if (!idSelect || !priceEl || !variantsEl) return;

  let variants = [];
  try { variants = JSON.parse(variantsEl.textContent || '[]'); } catch { variants = []; }
  if (!Array.isArray(variants) || !variants.length) return;

  // Symbol-only money (e.g. "$22.38", "€21.95") — no ISO currency codes.
  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'CAD';
    const locale = (navigator.language || 'en-CA').replace('_', '-');
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format((cents || 0) / 100);
    } catch {
      return ((cents || 0) / 100).toFixed(2);
    }
  };

  const setPrice = (priceCents, compareCents) => {
    const p = Number(priceCents || 0);
    const c = Number(compareCents || 0);
    const onSale = c > p && p > 0;

    priceEl.textContent = formatMoney(p);
    priceEl.classList.toggle('text-[var(--color-brand-strong)]', onSale);
    priceEl.classList.toggle('font-medium', onSale);
    priceEl.classList.toggle('text-[var(--color-charcoal)]', !onSale);

    if (!compareEl) return;
    if (onSale) {
      compareEl.textContent = formatMoney(c);
      compareEl.classList.remove('hidden');
    } else {
      compareEl.textContent = '';
      compareEl.classList.add('hidden');
    }
  };

  const setATCState = (available) => {
    // The id <select> sits outside the <form> and is linked with form="…", so closest('form')
    // is null; .form follows that attribute.
    const form = idSelect.form || idSelect.closest('form');
    const atc = form?.querySelector('button[type="submit"]');
    if (!atc) return;
    atc.disabled = !available;
    atc.classList.toggle('opacity-50', !available);
    atc.classList.toggle('cursor-not-allowed', !available);
    // Update only the label span so the icon isn't wiped
    const label = atc.querySelector('[data-atc-text]') || atc;
    label.textContent = available ? 'Add to cart' : 'Sold out';
    // No "Buy it now" for a sold-out variant
    const pay = form.querySelector('.sora-pay');
    if (pay) pay.hidden = !available;
  };

  const getSelections = () => {
    const picks = [];
    picker.querySelectorAll('[data-option-fieldset]').forEach((fs) => {
      const idx = Number(fs.getAttribute('data-option-index') || 0);
      const checked = fs.querySelector('input[type="radio"]:checked');
      picks[idx] = checked ? checked.value : null;
    });
    return picks;
  };

  const matches = (v, sel) => {
    if (sel[0] && v.option1 !== sel[0]) return false;
    if (sel[1] && v.option2 !== sel[1]) return false;
    if (sel[2] && v.option3 !== sel[2]) return false;
    return true;
  };

  const findMatchingVariant = (sel) => variants.find((v) => matches(v, sel)) || null;

  const findFirstAvailableForPartial = (sel) =>
    variants.find((v) => matches(v, sel) && v.available) || findMatchingVariant(sel);

  const applyVariant = (variant) => {
    if (!variant) return;
    idSelect.value = String(variant.id);
    setPrice(variant.price, variant.compare_at_price);
    setATCState(!!variant.available);

    // Ask the gallery to surface this variant's image as the first/hero image
    const fi = variant.featured_image;
    if (fi) {
      document.dispatchEvent(new CustomEvent('variant:image', {
        detail: { mediaId: fi.id != null ? String(fi.id) : null, src: fi.src || '' },
      }));
    }
  };

  // Reflect the chosen value in each option's legend
  const updateSelectedLabels = () => {
    picker.querySelectorAll('[data-option-fieldset]').forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      const label = fs.querySelector('[data-selected-label]');
      if (label) label.textContent = checked ? checked.value : '';
    });
  };

  // Grey out impossible values and strike through sold-out ones
  const refreshValueStates = (sel) => {
    picker.querySelectorAll('[data-option-fieldset]').forEach((fs) => {
      const idx = Number(fs.getAttribute('data-option-index') || 0);
      fs.querySelectorAll('input[type="radio"]').forEach((r) => {
        const test = sel.slice();
        test[idx] = r.value;

        const possible = variants.some((v) => matches(v, test));
        const anyAvailable = variants.some((v) => matches(v, test) && v.available);

        r.disabled = !possible;

        const pill = r.closest('[data-option-btn]')?.querySelector('[data-option-pill]');
        if (pill) {
          pill.classList.toggle('opacity-40', !possible);
          pill.classList.toggle('cursor-not-allowed', !possible);
          pill.classList.toggle('line-through', possible && !anyAvailable);
        }
      });
    });
  };

  const syncUIFromVariant = (variant) => {
    if (!variant) return;
    const optionVals = [variant.option1, variant.option2, variant.option3];

    picker.querySelectorAll('[data-option-fieldset]').forEach((fs, i) => {
      const val = optionVals[i];
      if (!val) return;
      const radio = fs.querySelector(`input[type="radio"][value="${CSS.escape(val)}"]`);
      if (radio) radio.checked = true;
    });

    const selections = getSelections();
    refreshValueStates(selections);
    updateSelectedLabels();
    applyVariant(variant);
  };

  const onChange = () => {
    const selections = getSelections();
    refreshValueStates(selections);
    updateSelectedLabels();

    let v = findMatchingVariant(selections);
    if (!v) v = findFirstAvailableForPartial(selections);
    if (v) syncUIFromVariant(v);
  };

  picker.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.type !== 'radio' || !t.name.startsWith('option-') || t.disabled) return;
    onChange();
  });

  const selectedId = idSelect.value;
  let initialVariant = variants.find((v) => String(v.id) === String(selectedId)) || null;
  if (!initialVariant) initialVariant = variants.find((v) => v.available) || variants[0];

  syncUIFromVariant(initialVariant);

  // Scroll affordance: arrow buttons + edge fade, only when the row overflows.
  const setupScrollAffordance = () => {
    const FADE = 28; // px of fade at a scrollable edge
    const EPS = 10; // tolerance: padding (px-2) + scroll-snap leave a few px at the extremes
    const update = (el, prev, next) => {
      const scrollable = el.scrollWidth > el.clientWidth + EPS;
      const atStart = el.scrollLeft <= EPS;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - EPS;

      const left = scrollable && !atStart ? `${FADE}px` : '0px';
      const right = scrollable && !atEnd ? `${FADE}px` : '0px';
      const mask = `linear-gradient(to right, transparent 0, #000 ${left}, #000 calc(100% - ${right}), transparent 100%)`;
      el.style.webkitMaskImage = mask;
      el.style.maskImage = mask;
      el.classList.toggle('is-scrollable', scrollable);

      [prev, next].forEach((b) => {
        if (!b) return;
        b.classList.toggle('hidden', !scrollable);
        b.classList.toggle('flex', scrollable);
      });
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    };

    picker.querySelectorAll('[data-option-values]').forEach((el) => {
      const wrap = el.closest('[data-scroll-wrap]');
      const prev = wrap && wrap.querySelector('[data-scroll-prev]');
      const next = wrap && wrap.querySelector('[data-scroll-next]');
      const step = () => Math.max(el.clientWidth * 0.8, 120);

      if (prev) prev.addEventListener('click', () => el.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => el.scrollBy({ left: step(), behavior: 'smooth' }));

      update(el, prev, next);
      el.addEventListener('scroll', () => update(el, prev, next), { passive: true });
      if (window.ResizeObserver) new ResizeObserver(() => update(el, prev, next)).observe(el);
    });
  };
  setupScrollAffordance();
})();
